// src/services/recordReturn.js
//
// Task #9085 — bounded, sanitized cross-record return context.
//
// Office / project / market record drawers swap in place (never stacked).
// The destination receives exactly ONE return entry through router state
// (`recordReturn`) describing the record it replaced, so it can render
// "‹ {previous record}" and restore that record's identity, tab and list
// context. No stacks: a swap from a destination replaces the entry with the
// record being left, and following "back" drops it entirely.
//
// State is untrusted (history entries can be crafted), so every read is
// sanitized: only known kinds, internal paths under the record namespaces,
// and short plain-text labels survive.

const KINDS = new Set(["office", "project", "market", "lease"]);
const PATH_RULES = {
  lease: /^\/leases\/(?!timeline(?:\?|$))[^/?#]+(?:\?[^#]*)?$/,
  office: /^\/places\/offices\/[^/?#]+(?:\?[^#]*)?$/,
  project: /^\/projects\/[^/?#]+(?:\?[^#]*)?$/,
  market: /^\/places\/markets\?[^#]*$/,
};
const MAX_LABEL = 80;
const MAX_PATH = 600;

function cleanLabel(value) {
  if (typeof value !== "string") return null;
  // eslint-disable-next-line no-control-regex
  const s = value.replace(/[\u0000-\u001f\u007f]/g, " ").replace(/\s+/g, " ").trim();
  if (!s) return null;
  return s.length > MAX_LABEL ? `${s.slice(0, MAX_LABEL - 1)}…` : s;
}

/** Sanitize an untrusted return entry. Returns null when unusable. */
export function sanitizeRecordReturn(entry) {
  if (!entry || typeof entry !== "object") return null;
  const kind = entry.kind;
  if (!KINDS.has(kind)) return null;
  const path = typeof entry.path === "string" ? entry.path : "";
  if (!path || path.length > MAX_PATH || path.startsWith("//") || !PATH_RULES[kind].test(path)) return null;
  const label = cleanLabel(entry.label);
  if (!label) return null;
  const id = typeof entry.id === "string" && entry.id.length <= 120 ? entry.id : null;
  return { kind, path, label, id };
}

/** Read the (single) return entry from router location state. */
export function readRecordReturn(state) {
  return sanitizeRecordReturn(state?.recordReturn);
}

/**
 * Router state for a cross-record swap. `from` describes the record being
 * left. Only `recordReturn` is carried — earlier entries are discarded.
 */
export function recordSwapState(from) {
  const entry = sanitizeRecordReturn(from);
  return entry ? { recordReturn: entry } : {};
}

/** Router state when following the back affordance: no entry (no stacks). */
export function recordBackState() {
  return {};
}
